import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ n }) {
    return (
        <span className="font-mono text-[#E63946]">
            {"★".repeat(n)}
            <span className="text-zinc-700">{"★".repeat(5 - n)}</span>
        </span>
    );
}

export default function ReviewsTab({ password }) {
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = filter === "all" ? `${API}/admin/reviews` : `${API}/admin/reviews?status=${filter}`;
            const res = await axios.get(url, { headers: { "X-Admin-Password": password } });
            setReviews(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load reviews");
        } finally {
            setLoading(false);
        }
    }, [filter, password]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => ({
        pending: reviews.filter((r) => r.status === "pending").length,
        approved: reviews.filter((r) => r.status === "approved").length,
        rejected: reviews.filter((r) => r.status === "rejected").length,
    }), [reviews]);

    const moderate = async (id, status) => {
        try {
            await axios.patch(
                `${API}/admin/reviews/${id}`,
                { status },
                { headers: { "X-Admin-Password": password } }
            );
            toast.success(status === "approved" ? "Approved · live on product page" : status === "rejected" ? "Rejected" : "Re-queued");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Permanently delete this review?")) return;
        try {
            await axios.delete(`${API}/admin/reviews/${id}`, { headers: { "X-Admin-Password": password } });
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="reviews-tab" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                {[
                    { k: "pending", label: "Pending", count: counts.pending },
                    { k: "approved", label: "Approved", count: counts.approved },
                    { k: "rejected", label: "Rejected", count: counts.rejected },
                    { k: "all", label: "All", count: reviews.length },
                ].map((f) => {
                    const active = filter === f.k;
                    return (
                        <button
                            key={f.k}
                            data-testid={`reviews-filter-${f.k}`}
                            onClick={() => setFilter(f.k)}
                            className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border ${
                                active
                                    ? "bg-[#E63946] border-[#E63946] text-white"
                                    : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                            }`}
                        >
                            {f.label} <span className="opacity-70">({f.count})</span>
                        </button>
                    );
                })}
            </div>

            {loading && <div className="text-zinc-500 text-sm py-12 text-center">Loading…</div>}

            {!loading && reviews.length === 0 && (
                <div className="bg-fgm-surface border border-white/10 p-12 text-center text-zinc-500 font-display tracking-widest uppercase">
                    No {filter !== "all" && filter} reviews
                </div>
            )}

            {!loading && reviews.length > 0 && (
                <div className="space-y-3">
                    {reviews.map((r) => (
                        <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-fgm-surface border border-white/10 p-5">
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <Stars n={r.rating} />
                                        <span className="font-display tracking-wider text-white text-sm">{r.title}</span>
                                        {r.verified_purchase && (
                                            <span className="text-[9px] font-display tracking-[0.25em] uppercase px-2 py-0.5 border border-green-500/40 text-green-400">
                                                ✓ Verified
                                            </span>
                                        )}
                                        <span className={`text-[9px] font-display tracking-[0.25em] uppercase px-2 py-0.5 border ${
                                            r.status === "approved" ? "border-green-500/40 text-green-400" :
                                            r.status === "rejected" ? "border-zinc-500/40 text-zinc-500" :
                                            "border-yellow-500/40 text-yellow-400"
                                        }`}>
                                            ● {r.status}
                                        </span>
                                    </div>
                                    <div className="text-xs text-zinc-500">
                                        {r.name} · <span className="font-mono">{r.email}</span> · {r.product_id} ·{" "}
                                        {new Date(r.created_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {r.status !== "approved" && (
                                        <button
                                            data-testid={`review-approve-${r.id}`}
                                            onClick={() => moderate(r.id, "approved")}
                                            className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
                                        >
                                            ✓ Approve
                                        </button>
                                    )}
                                    {r.status !== "rejected" && (
                                        <button
                                            data-testid={`review-reject-${r.id}`}
                                            onClick={() => moderate(r.id, "rejected")}
                                            className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border border-white/15 text-zinc-400 hover:text-white"
                                        >
                                            ✗ Reject
                                        </button>
                                    )}
                                    <button
                                        data-testid={`review-delete-${r.id}`}
                                        onClick={() => remove(r.id)}
                                        className="text-zinc-500 hover:text-[#E63946] text-lg leading-none px-2"
                                        aria-label="Delete"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{r.body}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
